"use client";

import { useEffect, useRef, useState } from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";

function FileIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function getFileKey(file: File) {
  return `${file.name}__${file.size}__${file.lastModified}`;
}

export type SelectedFilesStripProps = {
  files: File[];
  onPreview: (file: File) => void;
  onRemove: (index: number) => void;
  /** Max width (px) — usually the chat section width from ResizeObserver */
  sectionWidth?: number;
};

export function SelectedFilesStrip({
  files,
  onPreview,
  onRemove,
  sectionWidth = 0,
}: SelectedFilesStripProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const thumbUrlsRef = useRef<Record<string, string>>({});
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const dragRef = useRef({
    active: false,
    dragging: false,
    startX: 0,
    scrollLeft: 0,
  });

  const isInteractiveTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(
      target.closest("[data-file-strip-card]") ||
        target.closest("button"),
    );
  };

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const file of files) {
      const key = getFileKey(file);
      if (thumbUrlsRef.current[key]) {
        next[key] = thumbUrlsRef.current[key];
      } else if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        thumbUrlsRef.current[key] = url;
        next[key] = url;
      }
    }

    for (const key of Object.keys(thumbUrlsRef.current)) {
      if (!next[key]) {
        URL.revokeObjectURL(thumbUrlsRef.current[key]);
        delete thumbUrlsRef.current[key];
      }
    }

    setThumbUrls(next);
  }, [files]);

  useEffect(() => {
    return () => {
      for (const url of Object.values(thumbUrlsRef.current)) {
        URL.revokeObjectURL(url);
      }
      thumbUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;
      e.preventDefault();
      const maxScroll = el.scrollWidth - el.clientWidth;
      el.scrollLeft = Math.max(0, Math.min(el.scrollLeft + delta, maxScroll));
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [files.length]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || e.button !== 0) return;
    if (isInteractiveTarget(e.target)) return;

    dragRef.current = {
      active: true,
      dragging: false,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.active) return;
    const el = scrollRef.current;
    if (!el) return;

    const dx = e.clientX - dragRef.current.startX;
    if (!dragRef.current.dragging && Math.abs(dx) > 6) {
      dragRef.current.dragging = true;
      el.setPointerCapture(e.pointerId);
    }
    if (dragRef.current.dragging) {
      const maxScroll = el.scrollWidth - el.clientWidth;
      el.scrollLeft = Math.max(
        0,
        Math.min(dragRef.current.scrollLeft - dx, maxScroll),
      );
    }
  };

  const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (dragRef.current.dragging && el?.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    dragRef.current = {
      active: false,
      dragging: false,
      startX: 0,
      scrollLeft: 0,
    };
  };

  if (files.length === 0) return null;

  return (
    <div
      className="mb-0 shrink-0 min-w-0 -mx-4 px-4"
      style={sectionWidth > 0 ? { maxWidth: sectionWidth } : undefined}
    >
      <div
        ref={scrollRef}
        className="h-full w-full overflow-x-auto overflow-y-hidden overscroll-x-contain touch-pan-x scroll-px-[0.25rem_1rem]"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        <div className="inline-flex flex-nowrap gap-2 px-1 pb-3">
          {files.map((file, index) => {
            const fileExt =
              file.name.split(".").pop()?.toUpperCase() || "FILE";
            const isImage = file.type.startsWith("image/");
            const thumbUrl = thumbUrls[getFileKey(file)];

            return (
              <div
                key={`${file.name}-${index}`}
                data-file-strip-card
                className="flex shrink-0 cursor-pointer select-none items-center gap-3 rounded-xl border border-aera-offwhite bg-aera-white p-3 text-left transition-colors hover:bg-aera-offwhite"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => onPreview(file)}
              >
                <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-lg bg-aera-lightgray">
                  {isImage && thumbUrl ? (
                    <AeraImage
                      native
                      src={thumbUrl}
                      alt=""
                      width={28}
                      height={28}
                      className="h-7 w-7 object-cover pointer-events-none"
                      draggable={false}
                    />
                  ) : (
                    <FileIcon size={20} />
                  )}
                </div>
                <div className="min-w-0 max-w-[200px] flex-1">
                  <div
                    className="truncate text-sm font-medium text-aera-charcoal"
                    title={file.name}
                  >
                    {file.name}
                  </div>
                  <div className="text-xs text-aera-charcoal">{fileExt}</div>
                </div>
                <AeraButton
                  variant="danger"
                  size="xs"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(index);
                  }}
                  className="p-1!"
                  title="ลบไฟล์"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
